import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiImg, Icon, Skeleton, useBalanceContext } from 'components';
import { formatLocalizedCurrency } from 'helpers';

const TravelBalanceSummary = () => {
  const { t } = useTranslation();
  const { travelOrder, orderLoading } = useBalanceContext();

  const insurancePrice = useMemo(() => {
    if (!travelOrder?.insurances?.length === 0) return 0;
    const numberOfParticipants = travelOrder?.participants?.length;
    const total = (travelOrder?.insurances?.[0]?.amount ?? 0) * numberOfParticipants;
    return total;
  }, [travelOrder]);

  const totalPriceTTC = useMemo(() => {
    return Number(travelOrder?.order?.amount) + Number(insurancePrice);
  }, [travelOrder]);

  const totalPassengers = useMemo(() => {
    const count = travelOrder?.participants?.length || 0;
    return `${count} ${t('form:participant')}${count > 1 ? 's' : ''}`;
  }, [travelOrder]);

  return (
    <>
      {orderLoading && (
        <div>
          <Skeleton classNames="hidden lg:block lg:w-full lg:h-[37rem] lg:rounded-xl" />
          <Skeleton classNames="fixed bottom-2 bg-gray-100 left-0 w-full h-24 lg:hidden" />
        </div>
      )}

      {!orderLoading && !isEmpty(travelOrder) && (
        <div className="travel-balance-summary h-auto rounded-xl bg-white mb-12 lg:mb-4">
          <div>
            <div className="hidden lg:block rounded-t-xl shrink-0 overflow-hidden">
              <ApiImg src={''} width="370" height="200" />
            </div>

            {/* TRAVEL INFORMATIONS */}
            <div className="p-6 flex flex-col gap-3">
              <h3 className="uppercase font-semibold">{travelOrder?.stay?.title}</h3>
              <p className="uppercase">
                <Icon name="location-dot" iconStyle="fad" classes="text-yellow-500 mr-4" />
                {travelOrder?.stay?.city} - {travelOrder?.stay?.country}
              </p>
              <p>
                <Icon name="plane-departure" iconStyle="fad" classes="text-yellow-500 mr-2" />
                {travelOrder?.stay?.departureCity}
              </p>
              <p className="lowercase">
                <Icon name="user-group" iconStyle="fad" classes="text-yellow-500 mr-2" />
                {totalPassengers}
              </p>
              <>
                <p>{t('purchase:departDate', { date: travelOrder?.stay?.fromDate })}</p>
                <p>{t('purchase:backDate', { date: travelOrder?.stay?.toDate })}</p>
              </>
              <p>{travelOrder?.lodgings?.map((lodging) => `${lodging.quantity} ${lodging.title}`).join(', ')}</p>

              {/* PRICES */}
              <div className="flex justify-between mb-2 pt-4 border-t-2 border-gray-300">
                <p>{t('purchase:travel')} </p>
                <p className="font-bold">{formatLocalizedCurrency(travelOrder?.order?.amount)}</p>
              </div>

              <div className="flex justify-between mb-2">
                <p>{t('purchase:stepper.insurance')}</p>
                <p className="font-bold">{formatLocalizedCurrency(insurancePrice)}</p>
              </div>

              <div className="flex justify-between mb-2">
                <p>{t('purchase:totalPriceTTC')} </p>
                <p className="font-bold">{formatLocalizedCurrency(totalPriceTTC)}</p>
              </div>

              <div className="flex justify-between">
                <p>{t('purchase:balance.amountOutstanding')}</p>
                <p className="font-bold">{formatLocalizedCurrency(travelOrder?.order?.balance)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TravelBalanceSummary;
